import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Trophy, Edit, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { EditDialog } from '../components/EditDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ElectedCandidates = () => {
  const { token, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partyFilter, setPartyFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  const editFields = [
    { name: 'ward_no', label: language === 'en' ? 'Ward No' : 'प्रभाग क्रमांक', type: 'text' },
    { name: 'seat_no', label: language === 'en' ? 'Seat No' : 'जागा क्रमांक', type: 'text' },
    { name: 'reservation', label: t('reservation'), type: 'text' },
    { name: 'winner_name', label: t('winner') + ' ' + (language === 'en' ? 'Name' : 'नाव'), type: 'text' },
    { name: 'winner_gender', label: t('winner') + ' Gender', type: 'text' },
    { name: 'winner_party', label: t('winner') + ' ' + t('party'), type: 'text' },
    { name: 'winner_votes', label: t('winner') + ' ' + t('votes'), type: 'number' },
    { name: 'runner_up_name', label: t('runnerUp') + ' ' + (language === 'en' ? 'Name' : 'नाव'), type: 'text' },
    { name: 'runner_up_gender', label: t('runnerUp') + ' Gender', type: 'text' },
    { name: 'runner_up_party', label: t('runnerUp') + ' ' + t('party'), type: 'text' },
    { name: 'runner_up_votes', label: t('runnerUp') + ' ' + t('votes'), type: 'number' },
    { name: 'margin', label: t('margin'), type: 'number' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [data, partyFilter, searchTerm]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/elected-candidates`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...data];

    if (partyFilter !== 'all') {
      filtered = filtered.filter(item => item.winner_party === partyFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.winner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.ward_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.winner_party?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredData(filtered);
  };

  const parties = ['all', ...new Set(data.map(item => item.winner_party).filter(Boolean))];

  const handleEdit = (item) => {
    setCurrentItem(item);
    setEditDialogOpen(true);
  };

  const handleAdd = () => {
    setCurrentItem(null);
    setEditDialogOpen(true);
  };

  const handleSaveComplete = () => {
    fetchData();
  };

  if (loading) {
    return <div className={`text-center py-12 ${language === 'mr' ? 'marathi-text' : ''}`}>{t('loading')}</div>;
  }

  return (
    <div className="space-y-6" data-testid="elected-candidates-page">
      <div>
        <h1 className={`text-3xl font-bold text-primary mb-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
          {t('electedCandidates')}
        </h1>
        <p className={`text-muted-foreground ${language === 'mr' ? 'marathi-text' : ''}`}>
          {language === 'en' ? 'Statement 7: Winners and runner-ups' : 'विधान 7: विजेते आणि उपविजेते'}
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={language === 'en' ? 'Search by candidate, ward, or party...' : 'उमेदवार, प्रभाग किंवा पक्षानुसार शोधा...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={partyFilter} onValueChange={setPartyFilter}>
              <SelectTrigger className="w-full md:w-64" data-testid="party-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {parties.map(party => (
                  <SelectItem key={party} value={party}>
                    {party === 'all' ? (language === 'en' ? 'All Parties' : 'सर्व पक्ष') : party}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAuthenticated && (
              <Button onClick={handleAdd} className="gap-2">
                <Plus className="h-4 w-4" />
                {t('add')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle className={language === 'mr' ? 'marathi-text' : ''}>
            {filteredData.length} {language === 'en' ? 'Elected Candidates' : 'निवडून आलेले उमेदवार'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{t('ward')}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{t('reservation')}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{t('winner')}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{t('party')}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{t('votes')}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{t('runnerUp')}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{t('party')}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{t('margin')}</TableHead>
                  {isAuthenticated && <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{t('edit')}</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.length > 0 ? (
                  filteredData.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-semibold">{item.ward_no}</TableCell>
                      <TableCell>{item.reservation}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Trophy className="h-4 w-4 text-accent" />
                          <div>
                            <div className="font-medium">{item.winner_name}</div>
                            <div className="text-xs text-muted-foreground">{item.winner_gender}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-primary font-medium">{item.winner_party}</TableCell>
                      <TableCell className="font-semibold">{item.winner_votes?.toLocaleString()}</TableCell>
                      <TableCell>
                        <div>
                          <div>{item.runner_up_name}</div>
                          <div className="text-xs text-muted-foreground">{item.runner_up_gender}</div>
                        </div>
                      </TableCell>
                      <TableCell>{item.runner_up_party}</TableCell>
                      <TableCell className="text-accent font-semibold">{item.margin?.toLocaleString()}</TableCell>
                      {isAuthenticated && (
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={isAuthenticated ? 9 : 8} className="text-center py-8">
                      <div className={language === 'mr' ? 'marathi-text' : ''}>{t('noData')}</div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <EditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        item={currentItem}
        fields={editFields}
        title={currentItem ? t('edit') + ' ' + t('candidate') : t('add') + ' ' + t('candidate')}
        endpoint="elected-candidates"
        token={token}
        onSave={handleSaveComplete}
      />
    </div>
  );
};

export default ElectedCandidates;